// System-managed statuses for partner-mediated bookings (e.g., KiwiTaxi).
// These represent transient states owned by the backend ingestion pipeline
// and the operator cannot resolve them by editing the booking directly:
//
//   - 'Pending Ingestion': operator approved the offer; the scraper hasn't
//     yet accepted on the partner portal. Driver/operator assignment is
//     dangerous because the reservation may still expire upstream.
//   - 'Ingestion Failed': scraper retries exhausted. The partner-side
//     reservation likely doesn't exist; manual cleanup required.
//
// UI rule: treat these as read-only — disable assignment, editing, and
// status changes until the backend transitions out of them (Confirmed on
// success, or operator-initiated archive after manual cleanup).
const SYSTEM_MANAGED_STATUSES = ['Pending Ingestion', 'Ingestion Failed'];

export const isSystemManagedStatus = (status) => SYSTEM_MANAGED_STATUSES.includes(status);

export const systemManagedStatusLabel = (status) => {
  switch (status) {
    case 'Pending Ingestion':
      return 'Waiting for partner confirmation — actions disabled.';
    case 'Ingestion Failed':
      return 'Partner-side accept failed — handle manually before dispatching.';
    default:
      return '';
  }
};

/**
 * The badge class for a booking status — ALWAYS a `text-bg-*` pair, never a
 * bare `bg-*`.
 *
 * WHY THIS EXISTS: the Orders table rendered its status badge from an inline
 * ternary chain that covered six of the nine statuses and fell through to the
 * empty string. Bootstrap 5.3's `.badge` sets `--bs-badge-color: #fff` and no
 * background, so `class="badge "` is white text on the white table row —
 * 1.00:1, invisible until the row-hover grey brings it to 1.12:1, which is
 * how the bug was reported (Trello M5H44AvS, Oct 2026). Three statuses were
 * affected: 'Passenger No-Show', 'Driver No-Show' and the legacy 'Pending'.
 *
 * Two rules keep that from coming back, and both are load-bearing:
 *
 *  1. The DEFAULT must be a visible pair. Never "simplify" it back to '' or
 *     to 'text-bg-light' — a status this map has not met yet must still be
 *     readable, because the map is behind the API's enum, not ahead of it.
 *  2. Return `text-bg-*`, NOT the `bg-*`/react-bootstrap `<Badge bg>` form.
 *     `<Badge bg="light">` and `<Badge bg="warning">` emit `bg-light` /
 *     `bg-warning`, which set the background and leave the inherited white
 *     text — the same invisibility by a different route. Bootstrap derives
 *     every `.text-bg-*` foreground with `color-contrast()`, so each pair
 *     below is >= 4.50:1 (measured: primary 4.50, success 4.53, danger 4.53,
 *     secondary 4.69, info 10.72, warning 12.88, dark 15.43 — Bootstrap
 *     5.3.8 defaults, and App.scss overrides none of those theme colours).
 *
 * Statuses are the API's `Status` union (Booking.interface.m.ts). The six
 * colours that were already in the Orders table are kept exactly as they
 * were; only the three unmapped ones are new. Both no-shows are `dark`
 * because they are terminal like 'Cancelled' but are not a cancellation, and
 * the glanceability of that distinction is what the ticket asked for.
 *
 * This is the one copy for the Orders module. Five other screens still carry
 * their own divergent status->colour maps (VisitorTracking, Payments'
 * BookingReferenceSearch, Events' EventTimeline, Orders' OrderDetailsModal
 * and LinkBookingsPanel) and disagree with each other on Completed and on
 * the no-shows. Folding them in here is the right next change; it needs its
 * own review, because it alters what four other screens look like.
 */
const STATUS_BADGE_CLASSES = {
  Confirmed: 'text-bg-primary',
  Completed: 'text-bg-success',
  Request: 'text-bg-warning',
  // Legacy sibling of 'Request': still in the API's Status union, dropped
  // from the Zod enum. Mapped because it costs nothing and reaches the UI.
  Pending: 'text-bg-warning',
  Cancelled: 'text-bg-danger',
  'Driver No-Show': 'text-bg-dark',
  'Passenger No-Show': 'text-bg-dark',
  'Pending Ingestion': 'text-bg-info',
  'Ingestion Failed': 'text-bg-danger',
};

export const bookingStatusBadgeClass = (status) => STATUS_BADGE_CLASSES[status]
  || 'text-bg-secondary';
